import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { DivisionChange } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineS, LabelM } from '@sorare/core/src/atoms/typography';
import { RivalsLightning } from '@sorare/core/src/components/rivals/RepScore/RivalsLightning';
import { fantasy, glossary, rivals } from '@sorare/core/src/lib/glossary';
import { desktopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { LeaderboardStats_footballRivalsManager } from './__generated__/index.graphql';

const Root = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minMax(0, 1fr));
`;
const StyledVertical = styled(Vertical).attrs({ gap: 0.5 })`
  align-items: flex-start;
  @media ${desktopAndAbove} {
    align-items: center;
    text-align: center;
  }
`;
const RepWrapper = styled(Horizontal)`
  gap: 2px;
`;
const BoxPreview = styled.img`
  width: var(--triple-unit);
  height: var(--triple-unit);
  object-fit: contain;
`;

type RewardProps = {
  pictureUrl?: string;
  promoted?: boolean;
};
const Reward = ({ pictureUrl, promoted }: RewardProps) => {
  if (pictureUrl) {
    return <BoxPreview src={pictureUrl} alt="" />;
  }

  if (promoted) {
    return (
      <HeadlineS color="var(--c-rivals-friendlies)">
        <FormattedMessage {...fantasy.promotion} />
      </HeadlineS>
    );
  }

  return (
    <HeadlineS>
      <FormattedMessage
        id="LeaderboardStats.reward.none"
        defaultMessage="None"
      />
    </HeadlineS>
  );
};

type Props = { manager: LeaderboardStats_footballRivalsManager };

export const LeaderboardStats = ({ manager }: Props) => {
  const { currentRanking, currentLeaderboard } = manager;
  const { points, ranking, divisionChange } = currentRanking || {};
  const promoted = divisionChange === DivisionChange.PROMOTED;
  const boxUrl = currentLeaderboard?.leaderboardConfig.rankingRewardConfig.find(
    ({ rank }) => rank === ranking
  )?.probabilisticBundleConfiguration?.pictureUrl;

  return (
    <Root>
      <StyledVertical>
        <HeadlineS>
          {ranking ? (
            <FormattedMessage
              {...glossary.ordinal}
              values={{ ordinal: ranking }}
            />
          ) : (
            '-'
          )}
        </HeadlineS>
        <LabelM color="var(--c-nd-600)">
          <FormattedMessage {...rivals.leaderboardPosition} />
        </LabelM>
      </StyledVertical>
      <StyledVertical>
        <RepWrapper>
          <HeadlineS>
            {points ? <FormattedNumber value={points} /> : '-'}
          </HeadlineS>
          <RivalsLightning fill="var(--c-rivals-reputation)" />
        </RepWrapper>
        <LabelM color="var(--c-nd-600)">
          <FormattedMessage {...rivals.rep} />
        </LabelM>
      </StyledVertical>
      <StyledVertical>
        <Reward promoted={promoted} pictureUrl={boxUrl} />
        <LabelM color="var(--c-nd-600)">
          <FormattedMessage {...glossary.reward} />
        </LabelM>
      </StyledVertical>
    </Root>
  );
};

LeaderboardStats.fragments = {
  footballRivalsManager: gql`
    fragment LeaderboardStats_footballRivalsManager on FootballRivalsManager {
      id
      currentRanking {
        id
        ranking
        points
        divisionChange
      }
      currentLeaderboard {
        id
        leaderboardConfig {
          rankingRewardConfig {
            rank
            probabilisticBundleConfiguration {
              id
              pictureUrl
            }
          }
        }
      }
    }
  ` as TypedDocumentNode<LeaderboardStats_footballRivalsManager>,
};
